import type { ReactNode } from 'react';
import type { ApiState } from '../api/hooks';
import { ApiError } from '../api/client';
import { errorText, fieldErrorText, fieldLabel } from '../i18n/reasons.hu';

export function Notice({ kind = 'info', title, children, role }: { kind?: 'info' | 'warn' | 'err' | 'ok'; title?: string; children?: ReactNode; role?: string }) {
  const cls = kind === 'info' ? 'notice' : `notice ${kind}`;
  return (
    <div className={cls} role={role ?? (kind === 'err' || kind === 'warn' ? 'alert' : 'status')}>
      {title ? <b>{title}</b> : null}
      {children ? <div className="small">{children}</div> : null}
    </div>
  );
}

/** Real error states: a failure is named, never rendered as an empty page. */
export function ErrorNotice({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  if (error instanceof ApiError && error.status === 503 && error.reason === 'binance_credentials_unavailable') {
    return (
      <Notice kind="warn" title="A Binance API-kulcs nem érhető el">
        A MoneyFlow jelenleg nem tudja olvasni a Binance-et, ezért ez nem üres lista, hanem hiányzó adat. A kulcs a titokkezelőben (vault) van; amíg nem elérhető, a Binance-lekérések 503-at adnak. A kézi bevitel és a már naplózott adatok használhatók.
        {onRetry ? (
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn small" onClick={onRetry}>
              Újrapróbálom
            </button>
          </div>
        ) : null}
      </Notice>
    );
  }
  const status = error instanceof ApiError ? error.status : null;
  const reason = error instanceof ApiError ? error.reason : null;
  // Always Hungarian: the server's English `detail` and a JS error's message are for the console, not for the owner.
  return (
    <Notice kind="err" title={status ? `Hiba (${status})` : 'Hiba'}>
      {errorText(status, reason)}
      {error instanceof ApiError && error.fields.length ? (
        <ul className="small" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {error.fields.map((f, i) => (
            <li key={`${f.loc.join('.')}-${i}`}>
              <b>{fieldLabel(f.loc)}</b>: {fieldErrorText(f.msg)}
            </li>
          ))}
        </ul>
      ) : null}
      {onRetry ? (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="btn small" onClick={onRetry}>
            Újrapróbálom
          </button>
        </div>
      ) : null}
    </Notice>
  );
}

export function Loading({ label = 'Betöltés…' }: { label?: string }) {
  return (
    <div className="card" role="status" aria-live="polite" aria-busy="true">
      <span className="muted">{label}</span>
      <div className="skeleton" style={{ width: '60%' }} />
      <div className="skeleton" style={{ width: '40%' }} />
    </div>
  );
}

export function Async<T>({ state, children }: { state: ApiState<T>; children: (data: T) => ReactNode }) {
  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorNotice error={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading />;
  return <>{children(state.data)}</>;
}
